import React, { memo } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SectionHeading } from '../components/ui/SectionHeading';
import { motion } from 'framer-motion';
import { Shield, Award, Users, AlertCircle } from 'lucide-react';
const criteria = [{
  icon: Shield,
  title: 'Legal Registration & Licensing',
  points: ['Registered with Nepal Tourism Board', 'Member of TAAN (Trekking Agencies Association)', 'Valid business license and permits', 'Comprehensive insurance coverage']
}, {
  icon: Award,
  title: 'Experience & Track Record',
  points: ['Years in operation (minimum 5+ years)', 'Positive reviews on TripAdvisor, Google', 'Success rate and safety record', 'Specialization in your chosen trek']
}, {
  icon: Users,
  title: 'Guide Qualifications',
  points: ['Government-licensed trekking guides', 'First aid and high-altitude training', 'English proficiency', 'Local knowledge and cultural sensitivity']
}];
const redFlags = ['Significantly cheaper than competitors (too good to be true)', 'No physical office or verifiable address', 'Pressure to book immediately', 'Vague or incomplete itineraries', 'No mention of permits or insurance', 'Poor communication or unprofessional responses', 'No clear cancellation policy', 'Requests for full payment upfront'];
export function ChoosingAgencyPage() {
  return <div className="min-h-screen bg-white font-sans" data-id="element-899">
      <Navbar data-id="element-900" />

      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center" data-id="element-901">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070&auto=format&fit=crop")'
      }} data-id="element-902">
          <div className="absolute inset-0 bg-gradient-to-b from-indigo-900/70 via-indigo-900/50 to-indigo-900/80" data-id="element-903" />
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto" data-id="element-904">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex justify-center mb-6" data-id="element-905">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center" data-id="element-906">
              <Shield className="text-white" size={32} data-id="element-907" />
            </div>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-5xl md:text-6xl font-bold mb-6 tracking-tight" data-id="element-908">
            Choosing the Right Trekking Agency
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-xl md:text-2xl text-indigo-100 font-light max-w-2xl mx-auto" data-id="element-909">
            Make an informed decision for a safe and memorable trek
          </motion.p>
        </div>
      </div>

      {/* Overview */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-910">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-911">
          <SectionHeading title="Why Your Choice Matters" subtitle="Overview" data-id="element-912" />
          <div className="prose prose-lg max-w-none text-stone-700 leading-relaxed space-y-4" data-id="element-913">
            <p data-id="element-914">
              Choosing the right trekking agency can make the difference between
              a life-changing adventure and a disappointing experience. With
              hundreds of agencies operating in Nepal, it's crucial to know what
              to look for and what red flags to avoid.
            </p>
            <p data-id="element-915">
              A reputable agency ensures your safety, handles all logistics
              professionally, provides knowledgeable guides, and operates
              ethically. They invest in proper training, equipment, and fair
              treatment of staff. While they may cost more than budget
              operators, the value and peace of mind are worth every penny.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Criteria */}
      <section className="bg-stone-50 py-20 px-4" data-id="element-916">
        <div className="max-w-7xl mx-auto" data-id="element-917">
          <SectionHeading title="Key Selection Criteria" subtitle="What to look for" data-id="element-918" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-919">
            {criteria.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-md" data-id="element-920">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mb-6" data-id="element-921">
                  <item.icon size={28} className="text-white" data-id="element-922" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-4" data-id="element-923">
                  {item.title}
                </h3>
                <ul className="space-y-3" data-id="element-924">
                  {item.points.map((point, pointIndex) => <li key={pointIndex} className="flex items-start gap-3" data-id="element-925">
                      <span className="text-indigo-500 font-bold" data-id="element-926">✓</span>
                      <span className="text-stone-700" data-id="element-927">{point}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Questions to Ask */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-928">
        <SectionHeading title="Questions to Ask" subtitle="Before booking" data-id="element-929" />
        <div className="space-y-4" data-id="element-930">
          {['What is included in the package price? (meals, accommodation, permits, guides, porters)', 'What is your cancellation and refund policy?', 'Are guides licensed and trained in first aid?', 'What is the guide-to-trekker ratio?', 'What happens if I get altitude sickness or need to evacuate?', 'Do you have emergency communication equipment?', 'What are the accommodation standards along the route?', 'How do you ensure ethical treatment and fair wages for porters?', 'Can you provide references from recent clients?', 'What insurance do you carry?'].map((question, index) => <motion.div key={index} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05
        }} className="bg-indigo-50 rounded-xl p-6 border-l-4 border-indigo-600" data-id="element-931">
              <p className="text-stone-700 font-medium" data-id="element-932">{question}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Red Flags */}
      <section className="bg-red-50 py-20 px-4" data-id="element-933">
        <div className="max-w-4xl mx-auto" data-id="element-934">
          <SectionHeading title="Warning Signs to Avoid" subtitle="Red flags" data-id="element-935" />
          <div className="bg-white rounded-2xl p-8 shadow-lg" data-id="element-936">
            <div className="flex items-start gap-4 mb-6" data-id="element-937">
              <AlertCircle className="text-red-600 shrink-0" size={32} data-id="element-938" />
              <div data-id="element-939">
                <h3 className="text-xl font-bold text-red-900 mb-2" data-id="element-940">
                  Be Cautious If You See:
                </h3>
                <p className="text-red-700" data-id="element-941">
                  These warning signs often indicate unprofessional or
                  potentially unsafe operators
                </p>
              </div>
            </div>
            <ul className="space-y-3" data-id="element-942">
              {redFlags.map((flag, index) => <li key={index} className="flex items-start gap-3 py-2" data-id="element-943">
                  <span className="text-red-600 font-bold text-xl" data-id="element-944">×</span>
                  <span className="text-stone-700" data-id="element-945">{flag}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-946">
        <SectionHeading title="Why Himalayan Trekking?" subtitle="Our commitment" data-id="element-947" />
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 md:p-12" data-id="element-948">
          <div className="space-y-6" data-id="element-949">
            <p className="text-stone-700 leading-relaxed text-lg" data-id="element-950">
              We meet and exceed all the criteria outlined in this guide. With
              over 15 years of operation, full licensing, and thousands of
              successful treks, we've built our reputation on safety,
              professionalism, and genuine care for our clients.
            </p>
            <p className="text-stone-700 leading-relaxed text-lg" data-id="element-951">
              Our guides are government-licensed, first-aid certified, and
              passionate about sharing their culture and mountains. We maintain
              transparent pricing, comprehensive insurance, and ethical
              employment practices. Most importantly, we're here to answer all
              your questions and ensure you feel completely confident before
              booking.
            </p>
            <div className="pt-6" data-id="element-952">
              <a href="#why-choose-us" data-id="element-953">
                <button className="bg-indigo-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-indigo-700 transition-colors" data-id="element-954">
                  Learn More About Us
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white" data-id="element-955">
        <div className="max-w-4xl mx-auto text-center" data-id="element-956">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-957">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-958">
              Ready to Trek with Confidence?
            </h2>
            <p className="text-xl text-indigo-100 mb-8" data-id="element-959">
              Contact us with any questions—we're here to help you make the
              right choice
            </p>
            <a href="#contact" data-id="element-960">
              <button className="bg-white text-indigo-600 px-8 py-4 rounded-full font-semibold hover:bg-indigo-50 transition-colors" data-id="element-961">
                Get in Touch
              </button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-962" />
    </div>;
}